// Company Admin manual. The tenant owner on AdminLayout — passes every
// operation check; controls the whole organisation.

const manual = {
  key: 'company_admin',
  role: 'Company Admin',
  title: 'User Manual — Company Admin',
  subtitle:
    'For the company owner: set up the company, its people and its processes, and keep them running.',
  sections: [
    {
      id: 'welcome',
      title: 'Welcome',
      blocks: [
        'As Company Admin you own your organisation in TaskMagix. You set up departments, Work Roles and members; define how tasks flow and how reports are collected; monitor every team; and manage billing. You pass every permission check automatically — the whole admin panel is yours.',
        'You can delegate parts of this to Sub Admins by granting them specific operations. Three areas stay yours alone and can never be delegated: Billing, AI Provider Keys and SOP Integration.',
      ],
    },
    {
      id: 'page-guide',
      title: 'Page-by-Page Guide',
      blocks: [
        'This guide walks your sidebar top to bottom. For each menu: what the page is for, its buttons and actions, and its form fields. Add/Edit forms open in a dialog; row actions appear as icons at the end of each row.',

        { sub: 'Dashboard (/dashboard)' },
        { image: '/manual/ca-dashboard.png', caption: 'Dashboard — a company-wide summary: KPI tiles, growth charts, Action Needed, Workload Health and Quick Actions.' },
        'Your Dashboard is the first page after login. It summarises the whole company.',
        {
          list: [
            'KPI strip — To Do, Completed Tasks, Overdue Tasks and Active Members, each linking to the matching list.',
            'Analytics — Task Completion (a progress ring), Storage by File Type (a donut with a breakdown) and AI Usage (a meter of your monthly token quota).',
            'Growth — Company Signups (a per-day column chart with 15/30/45-day windows) and Plan Distribution.',
            'Billing — headline revenue tiles, shown as "Coming soon" placeholders.',
            'Action Needed — At-Risk Tasks (overdue, idle or due-today) and Team Compliance rings for Daily, Weekly and Monthly report submission rates.',
            'Workload Health — Task Status breakdown (Open / Overdue / Completed).',
            'Recent Activity — a cross-team feed of the latest events.',
            'Quick Actions — shortcuts such as Create Workspace, Add Department and Invite Member.',
          ],
        },

        { sub: 'Administration › Departments (/departments)' },
        { image: '/manual/ca-departments.png', caption: 'Administration › Departments — create and organise the departments that scope Spaces, reports and workflows.' },
        'Create and organise departments. Departments scope Spaces, reports and workflows.',
        {
          list: [
            'Add Department — opens the form.',
            'Row actions: Edit; Delete (confirmation required).',
            'Filters: search by name/description, a Workspace dropdown (multi-workspace tenants only), and Clear all.',
          ],
        },
        'Add / Edit Department form: Department Name (required, 2–120 chars, must contain a letter), Description (optional, ≤500 chars), Status (Active/Inactive), Workspaces (multi-workspace tenants only). When editing across workspaces, an "Apply these changes to matching Departments in other Workspaces" checkbox also appears.',

        { sub: 'Administration › Work Roles (/work-roles)' },
        { image: '/manual/ca-work-roles.png', caption: 'Administration › Work Roles — tick operations to define a role, and set the seniority ladder via Arrange Hierarchy.' },
        'Define custom roles by ticking operations, and set the seniority order. Two tabs: Roles and Analytics.',
        {
          list: [
            'Add Work Role — opens the form.',
            'Arrange Hierarchy — drag roles from Most senior to Most junior (appears once you have two or more roles); Save Hierarchy to keep the order.',
            'Row actions: Edit; Delete (blocked while members are assigned — reassign them first).',
          ],
        },
        'Add / Edit Work Role form: Work Role Name (required, must contain a letter), Description (optional), and an Access level choice (Self only / Department / Company-wide) that is set automatically from the operations you tick. The Advanced section shows all operations, grouped by module, with a Select-all per module and a live "N operations selected" count. Ticking an operation automatically includes the page it needs.',

        { sub: 'Administration › Members (/members)' },
        { image: '/manual/ca-members.png', caption: 'Administration › Members — invite people and manage them; each member carries a Work Role and a Department.' },
        'Invite, view and manage everyone in the company. A List view (editable table) and a Grid view (read-only task-signal cards) share one toolbar.',
        {
          list: [
            'Add Member — opens the invite form (top right).',
            'Row actions: Edit; Reset password (shows a one-time temporary password); Resend invitation (for a pending invite); Offboard (records a destination and is reversible) or Re-onboard; Suspend / re-activate.',
            'Filters: search by Full Name or Email ID, plus Department and Work Role dropdowns, and Clear all.',
            'List / Grid view toggle.',
          ],
        },
        'Add / Edit Member form:',
        {
          table: {
            head: ['Field', 'Notes'],
            rows: [
              ['Full Name', 'Required; 2–80 characters; must contain a letter.'],
              ['Email ID', 'Required; valid email (stored lower-case).'],
              ['Contact Number', 'Optional; validated phone number.'],
              ['Role', 'User or Sub Admin.'],
              ['Work Role', 'Required for the User role; chosen from your active Work Roles.'],
              ['Workspaces', 'Required for a User — tick each workspace and, optionally, its Department.'],
              ['Status', 'Active or Suspended; a Suspension Reason field appears when Suspended (0–500 chars).'],
            ],
          },
        },

        { sub: 'Administration › External Collaborators' },
        { image: '/manual/ca-external-collaborators.png', caption: 'Administration › External Collaborators — invite outside partners and grant them specific Spaces/Lists.' },
        'Invite and manage people outside your company. This page appears only when the external-collaboration feature is on. Send an email invite scoped to specific Lists/Spaces, set each collaborator’s access level, and revoke access when the engagement ends.',

        { sub: 'Space Explorer' },
        { image: '/manual/ca-space-explorer.png', caption: 'Space Explorer — browse every Space in the company (company-wide reach).' },
        'A searchable directory of every Space in the company, with an inline tree. Use it to jump into any team’s work; open a Space to reach its Overview / Lists / Board.',

        { sub: 'Configuration › Workflows (/workflows)' },
        { image: '/manual/ca-workflows.png', caption: 'Configuration › Workflows — define the task status columns each department uses.' },
        'Define the task-status flow and assign it per department. Each status is Active or Inactive with a colour; the last Active status counts as "done".',
        {
          verify:
            'You cannot deactivate a status that still has tasks. The exact button labels here (Add status / colour picker / per-department assignment) follow the standard add-edit-delete pattern; confirm against the live page.',
        },

        { sub: 'Configuration › Reminders & Schedule (/reminders-schedule)' },
        { image: '/manual/ca-reminders-schedule.png', caption: 'Configuration › Reminders & Schedule — task reminders and the company report cadence.' },
        'Turn Daily, Weekly and Monthly report reminders on or off and set their timing. Each one is a card with its own enable toggle; a floating Save Changes / Reset pill appears once you edit anything.',
        {
          list: [
            'Daily card: enable toggle, Active days (Sun–Sat strip, at least one), Reminder at (time), and a "remind 24 hours after a miss" toggle.',
            'Weekly card: enable toggle, Day of week, Reminder at, and a "notify admins 48 hours after a miss" toggle.',
            'Monthly card: enable toggle, Day of month (1–28), Reminder at, "Report covers" (Last month / Current month), and a "notify admins 3 days after a miss" toggle.',
            'Task reminders: a ladder of pre-due offsets (e.g. 1 day before, 2 hours before) with add / remove per row.',
          ],
        },
        {
          note: 'Times use the company timezone shown under each time field. The Task Type and per-department Item Status catalogues used inside reports are configured here / in Work Report Settings — set them up before expecting meaningful "completed" rollups.',
        },
        'Daily Task Assignment Digest — on the same page, a once-a-day email listing every task assigned to the team that day (new assignments AND reassignments), read at the send time. Turn it on and set the Send at time (default 12:00 in your company timezone).',
        {
          list: [
            'Recipients are automatic: you (the Company Admin) receive the whole company; each supervisor receives the tasks assigned to the team they oversee.',
            'Additional recipients — optionally add named people (Full Name + Email ID), each marked To / CC / BCC, who also receive the company-wide digest. CC/BCC only send alongside at least one To recipient.',
            'Nothing is sent on a day with no assignments.',
          ],
        },

        { sub: 'Configuration › Usage Report' },
        { image: '/manual/ca-usage-report.png', caption: 'Configuration › Usage Report — the per-company usage summary and its recipients.' },
        'Manage who receives report digests (name + email recipients), and view report usage / compliance.',

        { sub: 'Configuration › Gamification' },
        { image: '/manual/ca-gamification.png', caption: 'Configuration › Gamification — points, badges and leaderboard settings.' },
        'Set the points, badges and leaderboard rules (Company-Admin only; available if your plan allows).',

        { sub: 'Files & Links' },
        { image: '/manual/ca-files-links.png', caption: 'Files & Links — the company-wide library of files and links.' },
        'The company’s shared library of files and links. Includes search, per-file actions (download, rename, delete) and a storage-usage card.',

        { sub: 'Process › SOPs Library' },
        { image: '/manual/ca-sops-library.png', caption: 'Process › SOPs Library — publish and manage standard operating procedures.' },
        'Publish and manage Standard Operating Procedures (available if your plan allows). Open an SOP to read it or, where allowed, expand it into a reviewable task tree that you can save as a reusable blueprint.',

        { sub: 'Process › Task Monitor (/task-monitor)' },
        { image: '/manual/ca-task-monitor.png', caption: 'Process › Task Monitor — a company-wide view of every task with filters and inline edits.' },
        'Your live view of every task across the company, grouped into colour-coded status sections. List, Board and Gantt (timeline) views.',
        {
          list: [
            'List / Board / Gantt view toggle.',
            'Filters: search by Task Name, Space or List, plus Status, Department, Assignee, Priority, Repeat and Verification dropdowns, an Activity chip (Today / Yesterday / This week / This month / custom range — keeps tasks created or running in that window) and a Due date-range chip; Clear resets them.',
            'The Verification filter shows where each task stands with its checker: Verified (checker-approved), Awaiting checker (submitted, pending a decision), Rejected, or No verification (not routed to a checker).',
            'Status sections use your Space workflow colours; due dates are colour-coded — overdue in red, due today in amber, completed on time in green.',
            'List columns: Task Name, Department, Space, Assignees, Checker, Start Date, Start Time, Due Date, Due Time, Priority, Comments, Created By. Subtasks expand under their parent.',
            'Export CSV — downloads the tasks matching the current filters, with assignee names.',
          ],
        },

        { sub: 'Process › Work Reports' },
        { image: '/manual/ca-work-reports.png', caption: 'Process › Work Reports — review submitted Daily / Weekly / Monthly reports with compliance rings.' },
        'Audit every submitted report by user, date, workspace and status, and read the Daily / Weekly / Monthly compliance rings. Reported hours include task time plus meeting durations.',

        { sub: 'Analytics (/analytics)' },
        'A company-wide progress dashboard: KPI cards, a Task Status donut and an employee-wise table, all scoped to your company.',
        {
          list: [
            'Filters: Department and a Period selector (All time / Today / This week / This month / custom range). "Today" scopes to tasks created or running today.',
            'KPI cards: Employees, Tasks, Complete, To Do, In Progress, Overdue, Goals completed, Reports submitted and Verification pending.',
            'Task Status donut splits every task into To Do, In Progress, Complete and Overdue, coloured to match your Space workflow.',
            'Employee table shows per-person Assigned / Completed / Open / Overdue / Goals / Reports, with search, sortable columns and pagination.',
            'Export — download the table (CSV, Excel and more) honouring the active filters; employee name and email are included.',
          ],
        },

        { sub: 'Leaderboard' },
        { image: '/manual/ca-leaderboard.png', caption: 'Leaderboard — the company points ranking.' },
        'The same company points ranking members see (available if your plan allows gamification), with period and department filters.',

        { sub: 'AI Usage (/ai-usage)' },
        { image: '/manual/ca-ai-usage.png', caption: 'AI Usage — AI credit consumption across the company.' },
        'This month’s AI token spend. Shows the running total against your plan’s monthly quota, plus a recent-calls table (time, provider/model, tokens, feature, and whether the built-in generator was used). Refreshes automatically.',

        { sub: 'Logs › Audit / Login Activity / Email Status' },
        { image: '/manual/ca-logs.png', caption: 'Logs — the Audit, Login Activity and Email Status trails for your company.' },
        'Three read-only logs for investigation.',
        {
          list: [
            'Audit Log: who changed what (create / update / delete), by resource, with a timestamp.',
            'Login Activity: logins with IP address and online/offline status.',
            'Email Status Logs: outbound email delivery (sent / failed), with a failure reason and a Retry action.',
          ],
        },

        { sub: 'Integrations › SOP Integration (owner only)' },
        { image: '/manual/ca-sop-integration.png', caption: 'Integrations › SOP Integration — connect ProcessPro (owner only).' },
        'The ProcessPro connector that feeds the SOPs Library. Owner-only and never delegated. Configure the connection and sync here.',

        { sub: 'Integrations › AI Provider Keys (/api-keys, owner only)' },
        { image: '/manual/ca-ai-keys.png', caption: 'Integrations › AI Provider Keys — bring your own AI provider keys (owner only).' },
        'Bring your own AI keys so AI features run on your subscription. Owner-only; cannot be delegated.',
        {
          list: [
            'A tab per provider (Anthropic Claude / OpenAI / Google Gemini).',
            'Per provider: paste an API Key and optional Model, then Save (the key is verified live before storing); Test Connection re-checks a saved key; Remove deletes it; Make active selects the provider for report + workspace AI.',
            'Runtime fallback order card: the active provider is the pinned Primary. Add / reorder (↑↓) / remove configured providers to set which are tried if the active one hits a temporary error.',
          ],
        },

        { sub: 'Billing (/billing, owner only)' },
        { image: '/manual/ca-billing.png', caption: 'Billing — your current plan, invoices and upgrade options (company owner only).' },
        'View your current plan and invoices, and change plan. Stays reachable even when the account is billing-locked. Plan cards show each tier’s caps (users, workspaces, storage, AI tokens) with a Select / Upgrade button; the invoices table offers a Download PDF per invoice.',

        { sub: 'Refer & Earn (/referrals)' },
        { image: '/manual/ca-refer-earn.png', caption: 'Refer & Earn — your referral link and rewards.' },
        'Share your referral link (with a Copy button) and track Pending / Qualified / Rewarded counts. Qualifying sign-ups earn you a free plan extension.',

        { sub: 'Support tickets (/support-tickets, owner only)' },
        { image: '/manual/ca-support-tickets.png', caption: 'Support tickets — raise and track support requests (owner only).' },
        'Raise and track support requests with the platform team.',
        {
          list: [
            'Status tabs: All / Open / In progress / Waiting on you / Resolved / Closed (with counts).',
            'New ticket form: Subject (5–100 chars), Description (20–1000 chars), Category (General / Bug / Feature request / Billing), Priority (Urgent / High / Normal / Low), and up to 3 image attachments (≤1 MB each).',
            'In a ticket: reply with a message + attachments, change the status, or Close the ticket.',
          ],
        },

        { sub: 'Our Products' },
        { image: '/manual/ca-our-products.png', caption: 'Our Products — the 1XL product suite.' },
        'A read-only, informational catalogue of the provider’s other products.',

        { sub: 'Profile and My Preferences (from the user menu)' },
        { image: '/manual/ca-profile.png', caption: 'My Profile — your personal details and password.' },
        'Both are reached from your avatar menu, not the sidebar.',
        {
          list: [
            'My Profile — personal: avatar upload (JPEG/PNG/GIF/WebP, ≤2 MB), Full Name, Contact Number.',
            'My Profile — change password: Current, New (8–64 chars incl. upper, lower, number and symbol) and Confirm.',
            'My Preferences — your own display, accessibility, timezone and date, time and number formats.',
            'My Preferences → Region & formats — the company Country and Company Timezone, for the whole company. The timezone drives report reminders and the calendar day of date-only due dates. Each saves with its own button.',
            'The company task-reminder offsets are on Reminders & Schedule.',
          ],
        },
      ],
    },
    {
      id: 'daily-workflow',
      title: 'Daily Workflow',
      blocks: [
        {
          steps: [
            'Log in to your Dashboard and scan Action Needed — At-Risk Tasks and Team Compliance.',
            'Open Task Monitor to check progress across teams and unblock anything stalled.',
            'Review Work Reports / compliance rings; follow up with anyone missing today’s report.',
            'Handle Members changes (invites, role assignments, offboarding) as they arise.',
            'Check Logs (Audit / Login / Email) if you are investigating an issue.',
            'Keep an eye on AI Usage and Storage against your plan.',
          ],
        },
      ],
    },
    {
      id: 'task-management',
      title: 'Task Management',
      blocks: [
        { sub: 'Structure' },
        'Work is organised as Workspace → Space → Project (folder) → List → Task → Subtask. TaskMagix is single-workspace for most tenants, so you mostly work at the Space level and below. Each Project gets a default "Tasks List".',
        { sub: 'Departments and Workflows' },
        'A Space belongs to a department, and its department sets which task-status workflow applies. Configure statuses on the Workflows page: each status is Active or Inactive, the last Active status counts as "done", and you cannot deactivate a status that still has tasks.',
        { sub: 'SOPs, blueprints and approvals' },
        'Publish procedures in the SOPs Library. You can expand an SOP into a task tree, review it, and save it as a reusable blueprint. Space access levels and the sops operation control who may approve and create those tasks.',
        { sub: 'Delegation and rank' },
        'You sit outside the rank order, so you see and assign everyone. Members are bound by rank: they cannot delegate to someone more senior. Set the order on the Work Roles page.',
      ],
    },
    {
      id: 'reports',
      title: 'Reports',
      blocks: [
        'You own the reporting schedule for the whole company on the Reminders & Schedule page.',
        {
          list: [
            'Enable or disable Daily, Weekly and Monthly reporting independently.',
            'Set reminder times, the weekly deadline day, the monthly deadline day, and whether the monthly report covers the previous or current month.',
            'Maintain the Task Type catalogue and the per-department Item Status catalogue used inside reports.',
            'Add report recipients (name + email) to have submitted reports delivered on a schedule.',
          ],
        },
        'On the Work Reports page, audit every submitted report by user, date, workspace and status, and read the compliance rings. Reported hours include task time plus meeting durations.',
        {
          note: 'Task Type seeds empty and Item Status is keyed per department — set these up before expecting meaningful "completed" rollups.',
        },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      blocks: [
        'You receive member-level notifications for your own tasks and mentions, plus admin ones such as resource-limit warnings (AI usage and storage approaching their caps) and support-ticket activity. Announcements you broadcast reach every member’s bell.',
        'All notifications arrive at the header bell; open "View all notifications" for the full, filterable list.',
      ],
    },
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      blocks: [
        { sub: 'How do I let a Sub Admin help me?' },
        'On Members, make the person a Sub Admin and grant specific operations (e.g. members.manage, task_monitor.view). They then share your panel with only those areas enabled. Billing, AI Provider Keys and SOP Integration can never be granted.',
        { sub: 'Where do I change the company timezone or country?' },
        'In My Preferences → Region & formats (avatar menu → My Preferences) — not a Settings sidebar item.',
        { sub: 'A member says a page is missing from their sidebar.' },
        'Their Work Role lacks that operation. Edit the Work Role on the Work Roles page.',
        { sub: 'Why is my whole sidebar greyed out except Billing?' },
        'The account is billing-locked. Resolve billing to restore access; Billing stays clickable so you can.',
        { sub: 'Reports show low "completed" counts.' },
        'Check that Item Statuses are configured for the relevant departments — custom statuses not mapped as complete are not counted.',
      ],
    },
    {
      id: 'accountability',
      title: 'Task Accountability & AI Proof Verification',
      blocks: [
        'Any task can carry accountability settings. Set them in its detail panel — open a task inside a Space, under the "Accountability" heading: a Checker (who verifies the work), an Expected output (what the task should produce), and a Proof required toggle. When Proof required is on, the task cannot be completed until at least one file or link is attached as proof.',
        { sub: 'The verification flow' },
        {
          steps: [
            'The assignee does the work and, if proof is required, attaches it to the task.',
            'They click Submit for verification — the task moves to "Awaiting checker" and the named Checker is notified.',
            'The Checker opens the task and clicks Approve (the task completes) or Reject (a note is required; the task returns to the assignee as "Changes requested" to fix and submit again).',
          ],
        },
        {
          note: 'A task with a named Checker cannot be marked complete directly — it must be approved. Reminders nudge the Checker when a task waits too long, and tasks stuck awaiting verification, or rejected and not corrected, escalate to supervisors automatically.',
        },
        { sub: 'Who can verify' },
        'The named Checker (or a Company/Sub Admin) is the primary approver. When a task has no named Checker, a member who holds the "Verify tasks" Work-Role operation and supervises an assignee can act as the fallback approver. Grant that operation on the Work Roles page to whoever should be able to verify.',
        { sub: 'AI proof verification (optional, advisory)' },
        'When you enable it for your company, the AI reads the submitted proof (images / PDFs), compares it against the Expected output, and attaches an advisory verdict to the task — looks correct, incomplete, mismatched, suspicious, or needs human review — with a short summary and any concerns it found. It also flags a proof file identical to one reused from an earlier submission.',
        {
          note: 'The AI is ADVISORY ONLY — it never approves or rejects a task; the human Checker always makes the final decision. Enabling it forwards proof files to your configured AI provider, so it is off by default and opt-in per company.',
        },
        'See it all on the Task Verification board (Process › Task Verification): green = verified / completed, red = overdue / rejected / verification overdue, amber = awaiting checker or AI-flagged.',
      ],
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      blocks: [
        {
          list: [
            'Set up in order: Departments → Work Roles (and rank) → Members → Workflows → Reminders & Schedule.',
            'Grant Sub Admins the minimum operations they need, not the maximum.',
            'Define Task Types and per-department Item Statuses before enabling reporting, so rollups are meaningful.',
            'Use Task Monitor daily to catch at-risk work early; use compliance rings to keep reporting healthy.',
            'Watch AI Usage and Storage against your plan to avoid surprises.',
            'Keep the Work-Role rank order accurate — it drives who can see and delegate to whom.',
          ],
        },
      ],
    },
  ],
}

export default manual
